'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [currentYear, setCurrentYear] = useState<number>(2025);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerSections: FooterSection[] = [
    {
      title: 'Services',
      links: [
        { label: 'AI Agents Development', href: '/services' },
        { label: 'Contract Staffing', href: '/services' },
        { label: 'Data Engineering', href: '/services' },
        { label: 'Copilot Workshops', href: '/services' }
      ]
    },
    {
      title: 'Industries',
      links: [
        { label: 'Technology & SaaS', href: '/industries' },
        { label: 'Healthcare', href: '/industries' },
        { label: 'BFSI', href: '/industries' },
        { label: 'Retail', href: '/industries' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Careers', href: '/about' },
        { label: 'Contact', href: '/contact' },
        { label: 'Blog', href: '/about' }
      ]
    }
  ];

  const socialLinks = [
    { icon: 'EnvelopeIcon', href: 'mailto:info@venkalabs.com', label: 'Email' },
    { icon: 'PhoneIcon', href: 'tel:+1234567890', label: 'Phone' },
    { icon: 'MapPinIcon', href: '#', label: 'Location' }
  ];

  return (
    <footer className={`bg-primary text-primary-foreground ${className}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-3 mb-4">
              <div className="relative w-10 h-10 flex items-center justify-center">
                <AppImage
                  // src="/assets/images/logo_venka-1767350987369.png"
                  src="/assets/images/venklogonew.PNG"
                  alt="Invictosoft Logo"
                  className="w-full h-full object-contain"
                  width={40}
                  height={40}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-headline font-bold leading-tight">Invictosoft</span>
                <span className="text-xs font-body opacity-80 leading-tight">
                  Technology. Talent. Intelligence.
                </span>
              </div>
            </Link>
            <p className="text-sm opacity-80 mb-6 leading-relaxed max-w-md">
              Execution-focused technology partner delivering AI agents, contract staffing, and data engineering solutions. Microsoft & AWS certified.
            </p>
            <div className="flex items-center space-x-4 mb-6">
              <AppImage
                src="/assets/images/microsoft-partner.png"
                alt="Microsoft Partner"
                width={80}
                height={32}
                className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-base"
              />
              <AppImage
                src="/assets/images/Screenshot_2026-01-02_at_12.41.30_PM-1767340820772.png"
                alt="AWS Partner"
                width={60}
                height={32}
                className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-base"
              />
            </div>
          </div>

          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="text-sm font-headline font-bold mb-4">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      className="text-sm opacity-80 hover:opacity-100 hover:text-accent transition-all duration-base"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-6">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-base"
                  aria-label={social.label}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
            <p className="text-sm opacity-80">
              &copy; {currentYear} Invictosoft. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;